import { AavoForm } from "src/components/common/forms/AavoForm.tsx";
import { Controller, DeepPartial, FieldPath, UseControllerProps, useForm } from "react-hook-form";
import React from "react";
import { FormResult } from "src/components/common/forms/types.ts";
import {
	InputDialogFieldProps,
	InputDialogSelectionOption,
	InputDialogType,
	InputDialogValueType,
} from "src/components/common/dialogs/input/types.ts";
import { AavoTextField } from "src/components/common/inputFields/AavoTextField.tsx";
import { AavoCheckbox } from "src/components/common/inputFields/AavoCheckbox.tsx";
import { AavoDatePicker } from "src/components/common/inputFields/AavoDatePicker.tsx";
import { Dayjs } from "dayjs";
import { AavoDateTimePicker } from "src/components/common/inputFields/AavoDateTimePicker.tsx";
import { floatRule, integerRule, requireRule } from "src/components/common/forms/validation.ts";
import { SelectField } from "../../inputFields/SelectField.tsx";
import { AsyncSelectField } from "src/components/common/inputFields/AsyncSelectField.tsx";
import i18n from "i18next";

export interface InputDialogContentProps<T extends InputDialogType> {
	type: InputDialogType;
	fieldLabel: string,
	defaultValue: InputDialogValueType<T>;
	onCompleted: (result: FormResult<InputDialogValueType<T>>) => void;
	required?: boolean;
	fieldProps?: InputDialogFieldProps<T>;
}

export type InputDialogRules<T extends InputDialogType> = UseControllerProps<
	InputDialogFormValues<T>
>["rules"];

interface InputDialogFormValues<T extends InputDialogType> {
	value: InputDialogValueType<T>;
}

export const InputDialogContent = <T extends InputDialogType>({
	type,
	onCompleted,
	defaultValue,
	required,
	fieldProps,
	fieldLabel,
}: InputDialogContentProps<T>) => {
	const {
		control,
		handleSubmit,
		formState: { isDirty },
	} = useForm<InputDialogFormValues<T>>({
		mode: "all",
		defaultValues: {
			value: defaultValue,
		} as DeepPartial<InputDialogFormValues<T>>,
	});

	const submitForm = async (values: InputDialogFormValues<T>) => {
		return values.value;
	};

	const rules: InputDialogRules<T> = {
		...getInputFieldRules(type),
		...(required && requireRule()),
	};

	return (
		<AavoForm<InputDialogFormValues<T>, InputDialogValueType<T>>
			handleSubmit={handleSubmit}
			isDirty={isDirty}
			submit={submitForm}
			submitLabel={i18n.t("ok")}
			onCompleted={onCompleted}
		>
			<Controller
				control={control}
				name={"value" as FieldPath<InputDialogFormValues<T>>}
				rules={rules}
				render={({ field, fieldState }) =>
					renderInputField({
						type: type,
						label: fieldLabel,
						value: field.value,
						onChange: field.onChange,
						error: fieldState.error?.message,
						fieldProps: fieldProps,
					})
				}
			/>
		</AavoForm>
	);
};

interface RenderInputFieldParams<T extends InputDialogType> {
	type: InputDialogType;
	value: InputDialogValueType<T>;
	label: string;
	onChange: (value: any) => void;
	error: string | undefined;
	fieldProps: InputDialogFieldProps<T> | undefined;
}

const renderInputField = <T extends InputDialogType>(
	params: RenderInputFieldParams<T>,
): React.ReactElement => {
	const { type, value, onChange, error, fieldProps, label } = params;
	switch (type) {
		case "string":
			return (
				<AavoTextField
					value={value}
					onChange={onChange}
					error={error}
					multiline={fieldProps?.text?.multiLine}
				/>
			);
		case "integer":
		case "decimal":
			return <AavoTextField value={value} onChange={onChange} error={error} />;
		case "boolean":
			return (
				<AavoCheckbox
					checked={value as boolean}
					label={label}
					onChange={(_, checked) => {
						onChange(checked);
					}}
				/>
			);
		case "date":
			return (
				<AavoDatePicker
					errorMessage={error}
					value={value as Dayjs | null}
					onChange={onChange}
					disablePast={fieldProps?.date?.disablePast}
				/>
			);
		case "datetime":
			return (
				<AavoDateTimePicker
					errorMessage={error}
					value={value as Dayjs | null}
					onChange={onChange}
					disablePast={fieldProps?.date?.disablePast}
				/>
			);
		case "singleNumberSelect":
			return renderSelectField(params);
	}
};

const renderSelectField = <T extends InputDialogType>({
	value,
	onChange,
	fieldProps,
}: RenderInputFieldParams<T>) => {
	const optionsProvider = fieldProps?.selection?.options ?? [];

	const commonProps = {
		getOptionKey: (option: InputDialogSelectionOption<T>) => option.value as number,
		getOptionLabel: (option: InputDialogSelectionOption<T>) => option.label,
		label: "",
		value: value as number | null,
	};

	if (typeof optionsProvider === "function") {
		return (
			<AsyncSelectField
				{...commonProps}
				fetchOptions={({ searchQuery, currentSelection }) =>
					optionsProvider({
						searchQuery,
						currentSelection: currentSelection as InputDialogValueType<T>,
					})
				}
				onChange={(option) => {
					onChange(option?.value);
				}}
			/>
		);
	} else {
		return <SelectField options={optionsProvider} onChange={onChange} {...commonProps} />;
	}
};

const getInputFieldRules = <T extends InputDialogType>(
	type: InputDialogType,
): InputDialogRules<T> | undefined => {
	switch (type) {
		case "integer":
			return integerRule() as InputDialogRules<T>;
		case "decimal":
			return floatRule() as InputDialogRules<T>;
		default:
			return undefined;
	}
};
